import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef
} from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Switch, 
  FormControlLabel,
  MenuItem
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import * as d3 from 'd3';
import _ from 'lodash';

import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import * as XLSX from 'xlsx';

// React DnD importları
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { useAuth } from '../contexts/AuthContext';

const COLUMN_TYPE = 'COLUMN'; // Drag & Drop item type

// -------------------------------------------------------------------------------------
// Ortak Yardımcı Fonksiyonlar & Memo Destekleri
// -------------------------------------------------------------------------------------

const getColorArray = (scheme) => {
  switch (scheme) {
    case 'Default':
      return d3.schemeTableau10;
    case 'Pastel':
      return d3.schemePastel1;
    case 'Vivid':
      return d3.schemeCategory10;
    default:
      return d3.schemeTableau10;
  }
};

const shortenColumnName = (name, maxLength = 30) => {
  if (name.length > maxLength) {
    return name.slice(0, maxLength - 3) + '...';
  }
  return name;
};

const exportToExcel = (data, columns, fileName) => {
  const filteredData = data.map((row) => {
    const filteredRow = {};
    columns.forEach((col) => {
      filteredRow[col.headerName] = Array.isArray(row[col.field])
        ? row[col.field].join(', ')
        : row[col.field];
    });
    return filteredRow;
  });
  const ws = XLSX.utils.json_to_sheet(filteredData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, fileName);
};

// dizi alanı "explode" işlemi (gerektiğinde ekranda tekrarlanmaması için useMemo ile sarmalanabilir)
const explodeData = (data, column) => {
  return data.flatMap(row => {
    const value = row[column];
    if (Array.isArray(value)) {
      return value
        .filter(v => v !== null && v !== undefined)
        .map(v => ({ ...row, [column]: v }));
    } else {
      if (value === null || value === undefined) return [];
      return [{ ...row, [column]: value }];
    }
  });
};

// -------------------------------------------------------------------------------------
// Popover Filtre Bileşeni
// -------------------------------------------------------------------------------------

const HeaderFilter = ({ column, selectedValues, onFilterChange, columnValues }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempSelected, setTempSelected] = useState(selectedValues);

  const handleButtonClick = (event) => {
    setAnchorEl(event.currentTarget);
    setTempSelected(selectedValues);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onFilterChange(column.field, tempSelected);
    handleClose();
  };

  const handleToggleValue = (value) => {
    setTempSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  return (
    <div>
      <Button
        onClick={handleButtonClick}
        endIcon={<FilterListIcon fontSize="small" />}
        sx={{ p: 0, textTransform: 'none' }}
      >
        {column.headerName}
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: 250 }}>
          <Typography variant="subtitle1" gutterBottom>
            {column.headerName} - {t('filter.filter')}
          </Typography>
          <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
            {columnValues[column.field]?.map((value, index) => (
              <ListItem key={index} disablePadding>
                <ListItemButton onClick={() => handleToggleValue(value)}>
                  <Checkbox
                    edge="start"
                    checked={tempSelected.includes(value)}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                  <ListItemText primary={value} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Button size="small" onClick={handleClose}>
              {t('filter.cancel')}
            </Button>
            <Button size="small" variant="contained" onClick={handleApply}>
              {t('filter.apply')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </div>
  );
};

// -------------------------------------------------------------------------------------
// Draggable Selected Column Bileşeni (Tabloda seçili kolonlar için)
// -------------------------------------------------------------------------------------

function DraggableSelectedColumn({ column, index, moveColumn, removeColumn }) {
  const { t } = useTranslation();
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: COLUMN_TYPE,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: COLUMN_TYPE,
    hover: (item) => {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      moveColumn(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drag(drop(ref));

  return (
    <ListItem
      ref={ref}
      sx={{
        backgroundColor: isDragging ? '#f0f0f0' : 'inherit',
        cursor: 'move',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '&:hover': { backgroundColor: '#f5f5f5' },
      }}
    >
      <ListItemText primary={column.headerName} />
      <IconButton
        size="small"
        onClick={() => removeColumn(column.field)}
        aria-label={t('selectedColumns.remove')}
      >
        <CloseIcon fontSize="inherit" />
      </IconButton>
    </ListItem>
  );
}

function SelectedColumnsList({ selectedColumns, moveColumn, removeColumn }) {
  const { t } = useTranslation();
  const [, drop] = useDrop({
    accept: COLUMN_TYPE,
    drop: () => ({}),
  });

  return (
    <List ref={drop}>
      {selectedColumns.length === 0 && (
        <ListItem>
          <ListItemText primary={t('selectedColumns.noSelection')} />
        </ListItem>
      )}
      {selectedColumns.map((col, index) => (
        <DraggableSelectedColumn
          key={col.field}
          column={col}
          index={index}
          moveColumn={moveColumn}
          removeColumn={removeColumn}
        />
      ))}
    </List>
  );
}

function AvailableColumnsList({ availableColumns, onAddColumn }) {
  const { t } = useTranslation();
  return (
    <List>
      {availableColumns.length === 0 && (
        <ListItem>
          <ListItemText primary={t('availableColumns.allSelected')} />
        </ListItem>
      )}
      {availableColumns.map((col) => (
        <ListItem
          key={col.field}
          button
          onClick={() => onAddColumn(col.field)}
          sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
        >
          <ListItemText primary={col.headerName} />
        </ListItem>
      ))}
    </List>
  );
}

// -------------------------------------------------------------------------------------
// ChartsTab Bileşeni
// -------------------------------------------------------------------------------------

function ChartsTab({ reportData, allColumns, arrayFields }) {
  const { t } = useTranslation();

  // Chart ile ilgili State'ler
  const [chartType, setChartType] = useState('stackedBar');
  const [xAxisColumn, setXAxisColumn] = useState('');
  const [yAxisColumn, setYAxisColumn] = useState('');
  const [stackColumn, setStackColumn] = useState('');
  const [categoryColumn, setCategoryColumn] = useState('');
  const [selectedColumn, setSelectedColumn] = useState('');
  const [breakdownColumn, setBreakdownColumn] = useState('');
  const [pivotedData, setPivotedData] = useState(null);
  const [currentChart, setCurrentChart] = useState(null);
  const [colorScheme, setColorScheme] = useState('Default');

  // Kod tekrarını azaltmak için veri hesaplarını useCallback ile tanımlayabiliriz
  const aggregate = useCallback((rows) => {
    // Örneğin burada 'codini_id' üzerinden unique değer sayısını hesaplıyoruz
    const uniqueValues = new Set(rows.map(row => row.codini_id));
    return uniqueValues.size;
  }, []);

  // asıl chart oluşturma fonksiyonu
  const generateChart = useCallback(() => {
    if (!reportData || reportData.length === 0) return;

    // Ortak veri işleme fonksiyonu
    const handleStackedBar = () => {
      if (!xAxisColumn || !stackColumn) return null;
      let dataToUse = reportData;
      const isXArray = arrayFields.includes(xAxisColumn);
      const isStackArray = arrayFields.includes(stackColumn);
      if (isXArray) dataToUse = explodeData(dataToUse, xAxisColumn);
      if (isStackArray) dataToUse = explodeData(dataToUse, stackColumn);

      const grouped = _.groupBy(dataToUse, xAxisColumn);
      const subcategories = _.uniq(dataToUse.map(row => row[stackColumn]));
      const computedData = Object.entries(grouped).map(([category, rows]) => {
        const obj = { [xAxisColumn]: category };
        subcategories.forEach(sub => {
          const subRows = rows.filter(row => row[stackColumn] === sub);
          const aggregatedValue = aggregate(subRows);
          obj[sub] = aggregatedValue;
        });
        return obj;
      });
      setPivotedData(computedData);
      return (
        <StackedBarChart 
          data={computedData} 
          xAxisColumn={xAxisColumn} 
          subcategories={subcategories} 
          colorScheme={colorScheme}
        />
      );
    };

    const handleDonutChart = () => {
      if (!categoryColumn) return null;
      const isCategoryArray = arrayFields.includes(categoryColumn);
      const dataToUse = isCategoryArray
        ? explodeData(reportData, categoryColumn)
        : reportData;
      const grouped = _.groupBy(dataToUse, categoryColumn);
      const computedData = Object.entries(grouped).map(([category, rows]) => ({
        [categoryColumn]: category,
        value: aggregate(rows)
      }));
      setPivotedData(computedData);
      return (
        <DonutChart 
          data={computedData} 
          categoryColumn={categoryColumn} 
          colorScheme={colorScheme}
        />
      );
    };

    const handleFrequencyChart = () => {
      if (!selectedColumn) return null;
      let dataToUse = reportData;
      const isArrayField = arrayFields.includes(selectedColumn);
      if (isArrayField) {
        dataToUse = explodeData(dataToUse, selectedColumn);
      }
      const frequencyData = calculateFrequency(dataToUse, selectedColumn, breakdownColumn);
      return (
        <FrequencyChart 
          data={frequencyData} 
          mainColumn={selectedColumn} 
          breakdownColumn={breakdownColumn} 
          colorScheme={colorScheme}
        />
      );
    };

    const handleTreemap = () => {
      if (!categoryColumn) return null;
      let dataToUse = reportData;
      const isCategoryArray = arrayFields.includes(categoryColumn);
      if (isCategoryArray) dataToUse = explodeData(dataToUse, categoryColumn);

      if (breakdownColumn) {
        const isBreakdownArray = arrayFields.includes(breakdownColumn);
        if (isBreakdownArray) dataToUse = explodeData(dataToUse, breakdownColumn);
        const grouped = _.groupBy(dataToUse, categoryColumn);
        const computedData = Object.entries(grouped).map(([category, rows]) => {
          const subGrouped = _.groupBy(rows, breakdownColumn);
          return {
            name: category,
            children: Object.entries(subGrouped).map(([subCategory, subRows]) => ({
              name: subCategory,
              value: aggregate(subRows)
            }))
          };
        });
        setPivotedData(computedData);
        return (
          <TreemapChart 
            data={computedData} 
            categoryColumn={categoryColumn} 
            breakdownColumn={breakdownColumn}
            colorScheme={colorScheme}
          />
        );
      } else {
        const grouped = _.groupBy(dataToUse, categoryColumn);
        const computedData = Object.entries(grouped).map(([category, rows]) => ({
          name: category,
          value: aggregate(rows)
        }));
        setPivotedData(computedData);
        return (
          <TreemapChart 
            data={computedData} 
            categoryColumn={categoryColumn} 
            colorScheme={colorScheme}
          />
        );
      }
    };

    const handleBubbleChart = () => {
      if (!categoryColumn) return null;
      let dataToUse = reportData;
      const isCategoryArray = arrayFields.includes(categoryColumn);
      if (isCategoryArray) dataToUse = explodeData(dataToUse, categoryColumn);
    
      if (breakdownColumn) {
        const isBreakdownArray = arrayFields.includes(breakdownColumn);
        if (isBreakdownArray) dataToUse = explodeData(dataToUse, breakdownColumn);
        const grouped = _.groupBy(dataToUse, categoryColumn);
        const computedData = Object.entries(grouped).map(([category, rows]) => {
          const subGrouped = _.groupBy(rows, breakdownColumn);
          return {
            name: category,
            children: Object.entries(subGrouped).map(([subCategory, subRows]) => ({
              name: subCategory,
              value: aggregate(subRows)
            }))
          };
        });
        setPivotedData(computedData);
        return (
          <BubbleChart 
            data={computedData} 
            categoryColumn={categoryColumn} 
            breakdownColumn={breakdownColumn}
            colorScheme={colorScheme}
          />
        );
      } else {
        const grouped = _.groupBy(dataToUse, categoryColumn);
        const computedData = Object.entries(grouped).map(([category, rows]) => ({
          name: category,
          value: aggregate(rows)
        }));
        setPivotedData(computedData);
        return (
          <BubbleChart 
            data={computedData} 
            categoryColumn={categoryColumn} 
            colorScheme={colorScheme}
          />
        );
      }
    };

    const handleHeatmap = () => {
      if (!xAxisColumn || !yAxisColumn) return null;
      let dataToUse = reportData;
      const isXArray = arrayFields.includes(xAxisColumn);
      const isYArray = arrayFields.includes(yAxisColumn);
      if (isXArray) dataToUse = explodeData(dataToUse, xAxisColumn);
      if (isYArray) dataToUse = explodeData(dataToUse, yAxisColumn);
    
      // Pivotlama işlemi
      const grouped = _.groupBy(dataToUse, xAxisColumn);
      const yCategories = _.uniq(dataToUse.map(row => row[yAxisColumn]));
      const computedData = yCategories.map(yCat => {
        const obj = { [yAxisColumn]: yCat };
        Object.keys(grouped).forEach(xCat => {
          const rows = grouped[xCat].filter(row => row[yAxisColumn] === yCat);
          const aggregatedValue = aggregate(rows); // Unique codini_id sayısını hesaplar
          obj[xCat] = aggregatedValue;
        });
        return obj;
      });
      setPivotedData(computedData);
      return (
        <HeatmapChart 
          data={computedData} 
          xAxisColumn={xAxisColumn} 
          yAxisColumn={yAxisColumn} 
          colorScheme={colorScheme}
        />
      );
    };

    // chartType'e göre dallanma
    let newChart = null;
    switch (chartType) {
      case 'stackedBar':
        newChart = handleStackedBar();
        break;
      case 'donut':
        newChart = handleDonutChart();
        break;
      case 'frequency':
        newChart = handleFrequencyChart();
        break;
      case 'treemap':
        newChart = handleTreemap();
        break;
      case 'bubble':
        newChart = handleBubbleChart();
        break;
      case 'heatmap':
        newChart = handleHeatmap();
        break;
      default:
        newChart = null;
    }
    setCurrentChart(newChart);
  }, [
    reportData,
    arrayFields,
    xAxisColumn,
    stackColumn,
    categoryColumn,
    selectedColumn,
    breakdownColumn,
    chartType,
    colorScheme,
    aggregate
  ]);

  // Sık sık değişmeyecek parametreler için generateChart'ı memoya bağlamak yeterli.
  // Yine de, generateChart butonuna basılmadıkça CPU harcanmayacak.
  // Değişiklikler olduğunda butona basıldığında yeniden hesaplanıyor.
  
  return (
    <Box sx={{ p: 2, mb: 4 }}>
      <Typography variant="h6">{t('charts.settings')}</Typography>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>{t('charts.colorScheme')}</InputLabel>
        <Select 
          value={colorScheme} 
          onChange={(e) => setColorScheme(e.target.value)}
          label={t('charts.colorScheme')}
        >
          <MenuItem value="Default">Default</MenuItem>
          <MenuItem value="Pastel">Pastel</MenuItem>
          <MenuItem value="Vivid">Vivid</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>{t('charts.chartType')}</InputLabel>
        <Select 
          value={chartType} 
          onChange={(e) => setChartType(e.target.value)}
          label={t('charts.chartType')}
        >
          <MenuItem value="frequency">{t('charts.frequency')}</MenuItem>
          <MenuItem value="stackedBar">{t('charts.stackedBar')}</MenuItem>
          <MenuItem value="donut">{t('charts.donut')}</MenuItem>
          <MenuItem value="treemap">{t('charts.treemap')}</MenuItem>
          <MenuItem value="bubble">{t('charts.bubble')}</MenuItem>
          <MenuItem value="heatmap">{t('charts.heatmap')}</MenuItem>
        </Select>
      </FormControl>

      {/* stackedBar seçiliyse */}
      {chartType === 'stackedBar' && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('charts.xAxisCategory')}</InputLabel>
            <Select
              value={xAxisColumn}
              onChange={(e) => setXAxisColumn(e.target.value)}
              label={t('charts.xAxisCategory')}
            >
              {allColumns.map(col => (
                <MenuItem key={col.field} value={col.field}>
                  {col.headerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('charts.stackCategory')}</InputLabel>
            <Select
              value={stackColumn}
              onChange={(e) => setStackColumn(e.target.value)}
              label={t('charts.stackCategory')}
            >
              {allColumns.map(col => (
                <MenuItem key={col.field} value={col.field}>
                  {col.headerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}

      {/* donut seçiliyse */}
      {chartType === 'donut' && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t('charts.category')}</InputLabel>
          <Select
            value={categoryColumn}
            onChange={(e) => setCategoryColumn(e.target.value)}
            label={t('charts.category')}
          >
            {allColumns.map(col => (
              <MenuItem key={col.field} value={col.field}>
                {col.headerName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* frequency seçiliyse */}
      {chartType === 'frequency' && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('charts.mainColumn')}</InputLabel>
            <Select
              value={selectedColumn}
              onChange={(e) => setSelectedColumn(e.target.value)}
            >
              {allColumns.map(col => (
                <MenuItem key={col.field} value={col.field}>
                  {col.headerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="breakdown-column-label">{t('charts.breakdownColumn')}</InputLabel>
            <Select
              labelId="breakdown-column-label"
              value={breakdownColumn}
              onChange={(e) => setBreakdownColumn(e.target.value || null)}
              label={t('charts.breakdownColumn')}
              variant="outlined"
            >
              <MenuItem value="">{t('charts.notSelected')}</MenuItem>
              {allColumns
                .filter(col => col.field !== selectedColumn)
                .map(col => (
                  <MenuItem key={col.field} value={col.field}>
                    {col.headerName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </>
      )}

      {/* treemap seçiliyse */}
      {chartType === 'treemap' && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('charts.category')}</InputLabel>
            <Select
              value={categoryColumn}
              onChange={(e) => setCategoryColumn(e.target.value)}
              label={t('charts.category')}
            >
              {allColumns.map(col => (
                <MenuItem key={col.field} value={col.field}>
                  {col.headerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('charts.breakdownColumn')}</InputLabel>
            <Select
              value={breakdownColumn}
              onChange={(e) => setBreakdownColumn(e.target.value || '')}
              label={t('charts.breakdownColumn')}
            >
              <MenuItem value="">{t('charts.notSelected')}</MenuItem>
              {allColumns
                .filter(col => col.field !== categoryColumn)
                .map(col => (
                  <MenuItem key={col.field} value={col.field}>
                    {col.headerName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </>
      )}

      {chartType === 'bubble' && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('charts.category')}</InputLabel>
            <Select
              value={categoryColumn}
              onChange={(e) => setCategoryColumn(e.target.value)}
              label={t('charts.category')}
            >
              {allColumns.map(col => (
                <MenuItem key={col.field} value={col.field}>
                  {col.headerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('charts.breakdownColumn')}</InputLabel>
            <Select
              value={breakdownColumn}
              onChange={(e) => setBreakdownColumn(e.target.value || '')}
              label={t('charts.breakdownColumn')}
            >
              <MenuItem value="">{t('charts.notSelected')}</MenuItem>
              {allColumns
                .filter(col => col.field !== categoryColumn)
                .map(col => (
                  <MenuItem key={col.field} value={col.field}>
                    {col.headerName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </>
      )}
      {chartType === 'heatmap' && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('charts.xAxisCategory')}</InputLabel>
            <Select
              value={xAxisColumn}
              onChange={(e) => setXAxisColumn(e.target.value)}
              label={t('charts.xAxisCategory')}
            >
              {allColumns.map(col => (
                <MenuItem key={col.field} value={col.field}>
                  {col.headerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('charts.yAxisCategory')}</InputLabel>
            <Select
              value={yAxisColumn}
              onChange={(e) => setYAxisColumn(e.target.value)}
              label={t('charts.yAxisCategory')}
            >
              {allColumns.map(col => (
                <MenuItem key={col.field} value={col.field}>
                  {col.headerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}

      <Button variant="contained" onClick={generateChart}>
        {t('charts.generateChart')}
      </Button>
      {currentChart}
    </Box>
  );
}

const calculateFrequency = (data, mainColumn, breakdownColumn) => {
  const total = data.length;
  if (!breakdownColumn) {
    const counts = _.countBy(data, mainColumn);
    return Object.entries(counts).map(([category, count]) => ({
      [mainColumn]: category,
      frequency: (count / total) * 100
    }));
  }
  const grouped = _.groupBy(data, mainColumn);
  return Object.entries(grouped).map(([mainCategory, items]) => {
    const subCounts = _.countBy(items, breakdownColumn);
    const totalSub = items.length;
    return {
      [mainColumn]: mainCategory,
      ...Object.fromEntries(
        Object.entries(subCounts).map(([subCat, count]) => [
          subCat,
          (count / totalSub) * 100
        ])
      )
    };
  });
};

// -------------------------------------------------------------------------------------
// Chart Bileşenleri (Frequency, StackedBar, Donut, Treemap)
// -------------------------------------------------------------------------------------

function FrequencyChart({ data, mainColumn, breakdownColumn, colorScheme }) {
  const svgRef = useRef();
  const { t } = useTranslation();
  const margin = { top: 20, right: 160, bottom: 100, left: 150 };
  const width = 800;

  // Kullanıcının belirli ayarları
  const [showLegend, setShowLegend] = useState(true);
  const [sortOrder, setSortOrder] = useState('none');
  const [xAxisMode, setXAxisMode] = useState('fixed');

  // Grafik çizimini bir defa (veya bağımlılıkları değiştiğinde) yapmak için useEffect kullanıyoruz
  // Veri manipülasyonunu useMemo ile sarmalayarak gereksiz tekrar hesaplamaları engelliyoruz
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    let tempData = [...data];
    if (sortOrder !== 'none') {
      tempData.sort((a, b) => {
        const aValue = breakdownColumn
          ? _.sum(Object.values(_.omit(a, mainColumn)))
          : a.frequency;
        const bValue = breakdownColumn
          ? _.sum(Object.values(_.omit(b, mainColumn)))
          : b.frequency;
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      });
    }
    return tempData;
  }, [data, mainColumn, breakdownColumn, sortOrder]);

  useEffect(() => {
    if (!sortedData || sortedData.length === 0) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Y ekseni boyutunu dinamik yapmak için, satır sayısına göre height hesaplayabilirsiniz
    const dynamicChartHeight = sortedData.length * 30; 
    let subCategories = [];

    if (breakdownColumn) {
      subCategories = [
        ...new Set(
          sortedData.flatMap(d => 
            Object.keys(d).filter(k => k !== mainColumn)
          )
        )
      ];
    }

    // Legend için yer açmak
    const legendHeight = breakdownColumn && showLegend
      ? subCategories.length * 25 + 30
      : 0;

    const totalWidth = width + margin.left + margin.right;
    const totalHeight = margin.top + dynamicChartHeight + margin.bottom + legendHeight;

    const chartSvg = svg
      .attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const y = d3
      .scaleBand()
      .domain(sortedData.map(d => d[mainColumn]))
      .range([dynamicChartHeight, 0])
      .padding(0.1);

    let maxTotalFrequency;
    if (breakdownColumn) {
      maxTotalFrequency = d3.max(sortedData, d => _.sum(Object.values(_.omit(d, mainColumn))));
    } else {
      maxTotalFrequency = d3.max(sortedData, d => d.frequency);
    }

    const x = d3
      .scaleLinear()
      .domain([0, xAxisMode === 'fixed' ? 100 : maxTotalFrequency])
      .range([0, width]);

    let colorScale;
    if (breakdownColumn) {
      colorScale = d3
        .scaleOrdinal()
        .domain(subCategories)
        .range(getColorArray(colorScheme));
    }

    // Tek kategorili çubuklar
    if (!breakdownColumn) {
      chartSvg
        .selectAll('rect')
        .data(sortedData)
        .enter()
        .append('rect')
        .attr('y', d => y(d[mainColumn]))
        .attr('height', y.bandwidth())
        .attr('x', 0)
        .attr('width', d => x(d.frequency))
        .attr('fill', getColorArray(colorScheme)[0]);
    } 
    // Sub-categories yani çoklu segmentli (stack) çubuklar
    else {
      const stack = d3.stack().keys(subCategories);
      const stackedData = stack(sortedData);

      chartSvg
        .selectAll('.category')
        .data(stackedData)
        .enter()
        .append('g')
        .attr('fill', d => colorScale(d.key))
        .selectAll('rect')
        .data(d => d)
        .enter()
        .append('rect')
        .attr('y', d => y(d.data[mainColumn]))
        .attr('height', y.bandwidth())
        .attr('x', d => x(d[0]))
        .attr('width', d => x(d[1]) - x(d[0]));
    }

    // Üzerine metin değeri
    chartSvg
      .selectAll('.value-label')
      .data(sortedData)
      .enter()
      .append('text')
      .attr('class', 'value-label')
      .attr('x', d => 
        breakdownColumn 
          ? x(_.sum(Object.values(_.omit(d, mainColumn)))) + 5 
          : x(d.frequency) + 5
      )
      .attr('y', d => y(d[mainColumn]) + y.bandwidth() / 2)
      .attr('dy', '0.35em')
      .style('font-size', '12px')
      .text(d => 
        breakdownColumn 
          ? `${_.sum(Object.values(_.omit(d, mainColumn))).toFixed(1)}%`
          : `${d.frequency.toFixed(1)}%`
      );

    chartSvg
      .append('g')
      .attr('class', 'y-axis')
      .call(d3.axisLeft(y));

    chartSvg
      .append('g')
      .attr('transform', `translate(0,${dynamicChartHeight})`)
      .call(d3.axisBottom(x).ticks(5).tickFormat(d => `${d}%`));

    // Legend çizimi
    if (breakdownColumn && showLegend) {
      const legend = chartSvg
        .append('g')
        .attr('class', 'legend')
        .attr('transform', `translate(0,${dynamicChartHeight + 30})`);

      const legendItems = legend
        .selectAll('g')
        .data(subCategories)
        .enter()
        .append('g')
        .attr('transform', (d, i) => `translate(0,${i * 25})`);

      legendItems
        .append('rect')
        .attr('width', 19)
        .attr('height', 19)
        .attr('fill', d => colorScale(d));

      legendItems
        .append('text')
        .attr('x', 24)
        .attr('y', 9.5)
        .text(d => d);
    }
  }, [
    sortedData,
    mainColumn,
    breakdownColumn,
    xAxisMode,
    colorScheme,
    showLegend,
    margin,
    width,
    sortOrder
  ]);

  // İndirme fonksiyonu
  const handleDownload = useCallback(() => {
    if (!svgRef.current) return;
    const svgElement = svgRef.current.cloneNode(true);
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'frequency-chart.svg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showLegend}
              onChange={() => setShowLegend(!showLegend)}
            />
          }
          label={t('charts.showLegend')}
        />
        <Select
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          sx={{ ml: 2 }}
        >
          <MenuItem value="none">{t('charts.noSort')}</MenuItem>
          <MenuItem value="asc">{t('charts.sortAsc')}</MenuItem>
          <MenuItem value="desc">{t('charts.sortDesc')}</MenuItem>
        </Select>
        <Select
          value={xAxisMode}
          onChange={(e) => setXAxisMode(e.target.value)}
          sx={{ ml: 2 }}
        >
          <MenuItem value="fixed">{t('charts.fixedAxis')}</MenuItem>
          <MenuItem value="auto">{t('charts.autoAxis')}</MenuItem>
        </Select>
      </Box>
      <Button
        variant="contained"
        size="small"
        onClick={handleDownload}
        sx={{ position: 'absolute', right: 0, top: -40 }}
      >
        {t('charts.download')}
      </Button>
      <svg ref={svgRef} style={{ maxWidth: '100%', height: 'auto' }} />
    </Box>
  );
}

function StackedBarChart({ data, xAxisColumn, subcategories, colorScheme }) {
  const svgRef = useRef();
  const { t } = useTranslation();
  const margin = { top: 20, right: 160, bottom: 100, left: 60 };
  const width = 800;
  const height = 400;

  // Grafik çizimini bir useEffect içine alalım
  useEffect(() => {
    if (!data || data.length === 0) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const totalWidth = width + margin.left + margin.right;
    const totalHeight = height + margin.top + margin.bottom;
    
    const chartSvg = svg
      .attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const stack = d3.stack().keys(subcategories);
    const stackedData = stack(data);

    const xScale = d3
      .scaleBand()
      .domain(data.map(d => d[xAxisColumn]))
      .range([0, width])
      .padding(0.2);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(stackedData, d => d3.max(d, d => d[1]))])
      .nice()
      .range([height, 0]);

    const colorScale = d3
      .scaleOrdinal()
      .domain(subcategories)
      .range(getColorArray(colorScheme));

    chartSvg
      .selectAll('.category')
      .data(stackedData)
      .enter()
      .append('g')
      .attr('fill', d => colorScale(d.key))
      .selectAll('rect')
      .data(d => d)
      .enter()
      .append('rect')
      .attr('x', d => xScale(d.data[xAxisColumn]))
      .attr('y', d => yScale(d[1]))
      .attr('height', d => yScale(d[0]) - yScale(d[1]))
      .attr('width', xScale.bandwidth())
      .append('title')
      .text(d => `${d.key}: ${d[1] - d[0]}`);

    // Değer yazıları
    chartSvg
      .selectAll('.value-label')
      .data(stackedData.flat())
      .enter()
      .append('text')
      .attr('class', 'value-label')
      .attr('x', d => xScale(d.data[xAxisColumn]) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d[1]) + 15)
      .attr('text-anchor', 'middle')
      .style('font-size', '10px')
      .text(d => (d[1] - d[0] > 0 ? d[1] - d[0] : ''));

    chartSvg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.15em')
      .attr('transform', 'rotate(-45)');

    chartSvg.append('g').call(d3.axisLeft(yScale));

    // Legend
    const legend = chartSvg
      .append('g')
      .attr('font-family', 'sans-serif')
      .attr('font-size', 10)
      .attr('text-anchor', 'start')
      .selectAll('g')
      .data(subcategories)
      .enter()
      .append('g')
      .attr('transform', (d, i) => `translate(${width + 20},${i * 20})`);

    legend
      .append('rect')
      .attr('x', 0)
      .attr('width', 19)
      .attr('height', 19)
      .attr('fill', colorScale);

    legend
      .append('text')
      .attr('x', 24)
      .attr('y', 9.5)
      .attr('dy', '0.32em')
      .text(d => d);
  }, [data, xAxisColumn, subcategories, colorScheme]);

  const handleDownload = useCallback(() => {
    if (!svgRef.current) return;
    const svgElement = svgRef.current.cloneNode(true);
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'stacked-bar-chart.svg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      <Button
        variant="contained"
        size="small"
        onClick={handleDownload}
        sx={{ position: 'absolute', right: 0, top: -40 }}
      >
        {t('charts.download')}
      </Button>
      <svg ref={svgRef} style={{ maxWidth: '100%', height: 'auto' }} />
    </Box>
  );
}

function DonutChart({ data, categoryColumn, colorScheme }) {
  const svgRef = useRef();
  const { t } = useTranslation();
  const maxWidth = 400; 
  const maxHeight = 400; 
  const margin = 40;
  const legendItemHeight = 20;

  const [showLegend, setShowLegend] = useState(true);
  const [sortOrder, setSortOrder] = useState('none');
  const [showValues, setShowValues] = useState(false);
  const [showCategoryNames, setShowCategoryNames] = useState(false);

  // Veri sıralama işlemi
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    let tempData = [...data];
    if (sortOrder !== 'none') {
      tempData.sort((a, b) => {
        const aValue = a.value;
        const bValue = b.value;
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      });
    }
    return tempData;
  }, [data, sortOrder]);

  useEffect(() => {
    if (!sortedData || sortedData.length === 0) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = Math.min(maxWidth, window.innerWidth * 0.8);
    const height = Math.min(maxHeight, window.innerHeight * 0.8);
    const radius = Math.min(width, height) / 2 - margin;

    const legendHeight = showLegend ? sortedData.length * legendItemHeight : 0;
    const totalHeight = height + legendHeight + 40;

    svg.attr('viewBox', `0 0 ${width} ${totalHeight}`);

    const chartSvg = svg
      .append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`);

    const pie = d3.pie().value(d => d.value);
    const pieData = pie(sortedData);

    const colorScale = d3
      .scaleOrdinal()
      .domain(sortedData.map(d => d[categoryColumn]))
      .range(getColorArray(colorScheme));

    const arc = d3
      .arc()
      .innerRadius(radius * 0.5)
      .outerRadius(radius * 0.8);

    chartSvg
      .selectAll('path')
      .data(pieData)
      .enter()
      .append('path')
      .attr('d', arc)
      .attr('fill', d => colorScale(d.data[categoryColumn]))
      .attr('stroke', 'white')
      .style('stroke-width', '2px');

    // Değer veya yüzde yazma
    if (showValues) {
      chartSvg
        .selectAll('text.value')
        .data(pieData)
        .enter()
        .append('text')
        .attr('class', 'value')
        .attr('transform', d => `translate(${arc.centroid(d)})`)
        .attr('text-anchor', 'middle')
        .style('font-size', '8px')
        .text(d => d.data.value);
    } else {
      chartSvg
        .selectAll('text.percentage')
        .data(pieData)
        .enter()
        .append('text')
        .attr('class', 'percentage')
        .attr('transform', d => `translate(${arc.centroid(d)})`)
        .attr('text-anchor', 'middle')
        .style('font-size', '8px')
        .text(d => `${((d.endAngle - d.startAngle) / (2 * Math.PI) * 100).toFixed(1)}%`);
    }

    // Kategori isimleri (isteğe bağlı)
    if (showCategoryNames) {
      const outerArc = d3
        .arc()
        .innerRadius(radius * 0.9)
        .outerRadius(radius * 0.9);

      chartSvg
        .selectAll('text.category-name')
        .data(pieData)
        .enter()
        .append('text')
        .attr('transform', d => `translate(${outerArc.centroid(d)})`)
        .attr('text-anchor', 'middle')
        .style('font-size', calculateFontSize(sortedData))
        .text(d => d.data[categoryColumn]);
    }

    // Legend
    if (showLegend) {
      const legendSvg = svg
        .append('g')
        .attr('transform', `translate(${margin}, ${height + 20})`);

      const legend = legendSvg
        .selectAll('g')
        .data(pieData)
        .enter()
        .append('g')
        .attr('transform', (d, i) => `translate(0, ${i * legendItemHeight})`);

      legend
        .append('rect')
        .attr('width', 12)
        .attr('height', 12)
        .attr('fill', d => colorScale(d.data[categoryColumn]));

      legend
        .append('text')
        .attr('x', 24)
        .attr('y', 9.5)
        .attr('dy', '0.32em')
        .style('font-size', '8px')
        .text(d => `${d.data[categoryColumn]}: ${d.data.value}%`);
    }
  }, [
    sortedData,
    categoryColumn,
    colorScheme,
    showLegend,
    sortOrder,
    showValues,
    showCategoryNames,
    maxWidth,
    maxHeight,
    margin,
    legendItemHeight
  ]);

  const calculateFontSize = (data) => {
    const maxNameLength = Math.max(...data.map(d => d[categoryColumn].length));
    const baseFontSize = 12;
    return `${Math.min(baseFontSize, baseFontSize / (maxNameLength / 10))}px`;
  };

  const handleDownload = useCallback(() => {
    if (!svgRef.current) return;
    const svgElement = svgRef.current.cloneNode(true);
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'donut-chart.svg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showLegend}
              onChange={() => setShowLegend(!showLegend)}
            />
          }
          label={t('charts.showLegend')}
        />
        <Select
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          sx={{ ml: 2 }}
        >
          <MenuItem value="none">{t('charts.noSort')}</MenuItem>
          <MenuItem value="asc">{t('charts.sortAsc')}</MenuItem>
          <MenuItem value="desc">{t('charts.sortDesc')}</MenuItem>
        </Select>
        <Button
          variant="contained"
          size="small"
          onClick={() => setShowValues(!showValues)}
          sx={{ ml: 2 }}
        >
          {showValues ? t('charts.hideValues') : t('charts.showValues')}
        </Button>
        <Button
          variant="contained"
          onClick={() => setShowCategoryNames(!showCategoryNames)}
          sx={{ ml: 2 }}
        >
          {showCategoryNames ? 'Kategori İsimlerini Gizle' : 'Kategori İsimlerini Göster'}
        </Button>
      </Box>
      <Button
        variant="contained"
        size="small"
        onClick={handleDownload}
        sx={{ position: 'absolute', right: 0, top: -40 }}
      >
        {t('charts.download')}
      </Button>
      <svg ref={svgRef} style={{ maxWidth: '100%', height: 'auto' }} />
    </Box>
  );
}

function TreemapChart({ data, categoryColumn, breakdownColumn, colorScheme }) {
  const svgRef = useRef();
  const { t } = useTranslation();
  const margin = { top: 20, right: 20, bottom: 20, left: 20 };
  const width = 800;
  const height = 600;

  useEffect(() => {
    if (!data || data.length === 0) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // hiyerarşik veri
    const root = d3.hierarchy({ children: data })
      .sum(d => d.value || 0)
      .sort((a, b) => b.value - a.value);

    // treemap düzeni
    const treemap = d3.treemap()
      .size([width, height])
      .padding(1)
      .round(true);

    treemap(root);

    const colorScale = d3.scaleOrdinal()
      .domain(data.map(d => d.name))
      .range(getColorArray(colorScheme));

    const cell = svg
      .selectAll('g')
      .data(root.leaves())
      .enter()
      .append('g')
      .attr('transform', d => `translate(${d.x0},${d.y0})`);

    cell.append('rect')
      .attr('width', d => d.x1 - d.x0)
      .attr('height', d => d.y1 - d.y0)
      .attr('fill', d => {
        if (breakdownColumn) {
          return colorScale(d.parent.data.name);
        } else {
          return colorScale(d.data.name);
        }
      });

    cell.append('text')
      .attr('x', 5)
      .attr('y', 15)
      .each(function(d) {
        const text = d3.select(this);
        if (breakdownColumn) {
          text.append('tspan')
            .text(d.parent.data.name)
            .attr('x', 5)
            .attr('dy', '0em')
            .style('font-size', '12px')
            .style('fill', 'white');

          text.append('tspan')
            .text(d.data.name)
            .attr('x', 5)
            .attr('dy', '1.2em')
            .style('font-size', '12px')
            .style('fill', 'white');
        } else {
          text.append('tspan')
            .text(d.data.name)
            .attr('x', 5)
            .attr('dy', '0em')
            .style('font-size', '12px')
            .style('fill', 'white');
        }
      });

    cell.append('text')
      .attr('x', 5)
      .attr('y', 45)
      .text(d => {
        const rectWidth = d.x1 - d.x0;
        const rectHeight = d.y1 - d.y0;
        if (rectWidth > 50 && rectHeight > 30) {
          return d.data.value;
        }
        return '';
      })
      .style('font-size', '12px')
      .style('fill', 'white')
      .style('font-weight', 'bold');
  }, [data, categoryColumn, breakdownColumn, colorScheme]);

  const handleDownload = useCallback(() => {
    if (!svgRef.current) return;
    const svgElement = svgRef.current.cloneNode(true);
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'treemap-chart.svg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      <Button
        variant="contained"
        size="small"
        onClick={handleDownload}
        sx={{ position: 'absolute', right: 0, top: -40 }}
      >
        {t('charts.download')}
      </Button>
      <svg 
        ref={svgRef} 
        width={width + margin.left + margin.right} 
        height={height + margin.top + margin.bottom} 
      />
    </Box>
  );
}

function BubbleChart({ data, categoryColumn, breakdownColumn, colorScheme }) {
  const svgRef = useRef();
  const { t } = useTranslation();
  const margin = { top: 20, right: 20, bottom: 20, left: 20 };
  const width = 800;
  const height = 600;

  useEffect(() => {
    if (!data || data.length === 0) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Hiyerarşik veri hazırlama
    const rootData = { name: 'root', children: data };

    // Hiyerarşi oluşturma
    const root = d3.hierarchy(rootData)
      .sum(d => d.value || 0)
      .sort((a, b) => b.value - a.value);

    // Pack layout uygulama
    const pack = d3.pack()
      .size([width, height])
      .padding(3);
    pack(root);

    // Düğümleri alma (root hariç)
    const nodes = root.descendants().filter(d => d.depth > 0);

    // Renk skalası
    const colorScale = d3.scaleOrdinal()
      .domain(root.children.map(d => d.data.name))
      .range(getColorArray(colorScheme));

    // Daireleri çizme
    svg.selectAll('circle')
      .data(nodes)
      .enter()
      .append('circle')
      .attr('cx', d => d.x)
      .attr('cy', d => d.y)
      .attr('r', d => d.r)
      .attr('fill', d => {
        if (d.children) {
          return 'none'; // İç düğümler doldurulmaz
        } else {
          const categoryName = d.depth === 1 ? d.data.name : d.parent.data.name;
          return colorScale(categoryName); // Yaprak düğümler kategoriye göre doldurulur
        }
      })
      .attr('stroke', d => colorScale(d.depth === 1 ? d.data.name : d.parent.data.name))
      .attr('stroke-width', 2);

    // Etiket ekleme
    svg.selectAll('text')
      .data(nodes)
      .enter()
      .append('text')
      .attr('x', d => d.x)
      .attr('y', d => d.y)
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .text(d => {
        if (d.r > 20) { // Yarıçap 20'den büyükse etiket göster
          return d.data.name;
        }
        return '';
      })
      .style('font-size', '12px')
      .style('fill', 'black');

  }, [data, categoryColumn, breakdownColumn, colorScheme]);

  const handleDownload = useCallback(() => {
    if (!svgRef.current) return;
    const svgElement = svgRef.current.cloneNode(true);
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bubble-chart.svg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      <Button
        variant="contained"
        size="small"
        onClick={handleDownload}
        sx={{ position: 'absolute', right: 0, top: -40 }}
      >
        {t('charts.download')}
      </Button>
      <svg 
        ref={svgRef} 
        width={width + margin.left + margin.right} 
        height={height + margin.top + margin.bottom} 
      />
    </Box>
  );
}

function HeatmapChart({ data, xAxisColumn, yAxisColumn, colorScheme }) {
  const svgRef = useRef();
  const { t } = useTranslation();
  const margin = { top: 20, right: 20, bottom: 100, left: 100 };
  const width = 800;
  const height = 600;

  useEffect(() => {
    if (!data || data.length === 0) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // X ve Y kategorilerini alma
    const xCategories = _.uniq(Object.keys(data[0]).filter(k => k !== yAxisColumn));
    const yCategories = data.map(d => d[yAxisColumn]);

    // Veri matrisini oluşturma
    const matrix = yCategories.map(yCat => {
      const row = data.find(d => d[yAxisColumn] === yCat);
      return xCategories.map(xCat => row ? row[xCat] || 0 : 0);
    });

    // Renk skalası
    const maxValue = d3.max(matrix.flat());
    const colorScale = d3.scaleSequential(d3.interpolateBlues)
      .domain([0, maxValue]);

    // X ve Y ölçekleri
    const xScale = d3.scaleBand()
      .domain(xCategories)
      .range([0, width])
      .padding(0.05);

    const yScale = d3.scaleBand()
      .domain(yCategories)
      .range([0, height])
      .padding(0.05);

    // SVG elementi
    const chartSvg = svg
      .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Heatmap hücreleri
    chartSvg.selectAll()
      .data(matrix.flatMap((row, i) => row.map((value, j) => ({ value, x: j, y: i }))))
      .enter()
      .append('rect')
      .attr('x', d => xScale(xCategories[d.x]))
      .attr('y', d => yScale(yCategories[d.y]))
      .attr('width', xScale.bandwidth())
      .attr('height', yScale.bandwidth())
      .style('fill', d => colorScale(d.value));

    // X ekseni
    chartSvg.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.15em')
      .attr('transform', 'rotate(-45)');

    // Y ekseni
    chartSvg.append('g')
      .call(d3.axisLeft(yScale));

  }, [data, xAxisColumn, yAxisColumn, colorScheme]);

  const handleDownload = useCallback(() => {
    if (!svgRef.current) return;
    const svgElement = svgRef.current.cloneNode(true);
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'heatmap.svg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      <Button
        variant="contained"
        size="small"
        onClick={handleDownload}
        sx={{ position: 'absolute', right: 0, top: -40 }}
      >
        {t('charts.download')}
      </Button>
      <svg ref={svgRef} style={{ maxWidth: '100%', height: 'auto' }} />
    </Box>
  );
}

// -------------------------------------------------------------------------------------
// Tüm Kod Parçası: ReportDashboard
// -------------------------------------------------------------------------------------

export default function ReportDashboard() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { reportData = [], projectID, repospense_id, projectCode } = location.state || {};
  const [tabValue, setTabValue] = useState(0);
  const [columnValues, setColumnValues] = useState({});
  const [filters, setFilters] = useState({});
  const [filterModel, setFilterModel] = useState({ items: [] });

  const { currentUser } = useAuth();

  // hangi alanların array olduğunu belirle
  const arrayFields = useMemo(() => {
    if (!reportData.length) return [];
    return Object.keys(reportData.reduce((acc, row) => {
      Object.entries(row).forEach(([key, value]) => {
        acc[key] = acc[key] || Array.isArray(value);
      });
      return acc;
    }, {}));
  }, [reportData]);
  

  // columnValues hesapla (filtre popover'ında kullanılacak)
  useEffect(() => {
    if (reportData?.length) {
      const values = {};
      Object.keys(reportData[0]).forEach((key) => {
        const isArray = arrayFields.includes(key);
        const allValues = reportData
          .map((row) => row[key])
          .flatMap((val) => (isArray ? val : [val]));
        values[key] = Array.from(new Set(allValues)).filter((v) => v !== undefined);
      });
      setColumnValues(values);
    }
  }, [reportData, arrayFields]);

  // Filtre modeli oluştur
  useEffect(() => {
    const filterItems = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        if (arrayFields.includes(field)) {
          filterItems.push({
            field,
            operator: 'includesAny',
            value: values,
          });
        } else {
          values.forEach((value) => {
            filterItems.push({
              field,
              operator: 'equals',
              value,
            });
          });
        }
      }
    });
    setFilterModel({ items: filterItems, logicOperator: 'or' });
  }, [filters, arrayFields]);

  // Tüm olası kolonları oluştur
  const allGridColumns = useMemo(() => {
    if (!reportData.length) return [];

    const allKeys = reportData.reduce((acc, row) => {
      Object.keys(row).forEach(key => acc.add(key));
      return acc;
    }, new Set());

    return Array.from(allKeys).map((key) => {
      const isArray = reportData.some(row => Array.isArray(row[key]));
      return {
        field: key,
        headerName: shortenColumnName(key.toUpperCase()),
        flex: 1,
        minWidth: 150,
        filterable: false,
        disableColumnMenu: true,
        renderHeader: (params) => (
          <HeaderFilter
            column={params.colDef}
            selectedValues={filters[key] || []}
            onFilterChange={(field, newValues) =>
              setFilters((prev) => ({ ...prev, [field]: newValues }))
            }
            columnValues={columnValues}
          />
        ),
        renderCell: isArray
          ? (params) => {
              const value = params.value;
              if (!Array.isArray(value)) return value;
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {value.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        backgroundColor: '#e0e0e0',
                        borderRadius: '4px',
                        padding: '2px 8px',
                        margin: '2px',
                        fontSize: '0.875rem',
                      }}
                    >
                      {item}
                    </Box>
                  ))}
                </div>
              );
            }
          : undefined,
        filterOperators: isArray
          ? [
              {
                label: 'Includes Any',
                value: 'includesAny',
                getApplyFilterFn: (filterItem) => {
                  if (!filterItem?.value?.length) return null;
                  return (cellValue) => {
                    const arrValue = Array.isArray(cellValue) ? cellValue : [];
                    return arrValue.some((item) => filterItem.value.includes(item));
                  };
                },
              },
            ]
          : undefined,
      };
    });
  }, [reportData, arrayFields, filters, columnValues]);

  // Seçili kolonlar
  const [selectedColumns, setSelectedColumns] = useState([]);

  // Kullanılabilir (henüz seçilmemiş) kolonlar
  const availableColumns = useMemo(() => {
    const selectedFields = selectedColumns.map((c) => c.field);
    return allGridColumns.filter((col) => !selectedFields.includes(col.field));
  }, [allGridColumns, selectedColumns]);

  // Drag & Drop ile kolon sırasını değiştirme
  const moveColumn = useCallback((dragIndex, hoverIndex) => {
    setSelectedColumns((prevCols) => {
      const updated = [...prevCols];
      const [removed] = updated.splice(dragIndex, 1);
      updated.splice(hoverIndex, 0, removed);
      return updated;
    });
  }, []);

  // Kolon ekleme
  const addColumn = useCallback((field) => {
    setSelectedColumns((prev) => {
      if (prev.some((col) => col.field === field)) return prev;
      const colToAdd = allGridColumns.find((c) => c.field === field);
      if (!colToAdd) return prev;
      return [...prev, colToAdd];
    });
  }, [allGridColumns]);

  // Kolon kaldırma
  const removeColumn = useCallback((field) => {
    setSelectedColumns((prev) => prev.filter((col) => col.field !== field));
  }, []);

  const dataGridColumns = selectedColumns;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getRowId = useCallback(
    (row, index) => row.codini_id || index,
    []
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ backgroundColor: '#f4f4f4', minHeight: '100vh', padding: '20px' }}>
        <Container sx={{ mt: 1, backgroundColor: '#fff', borderRadius: 2, padding: '3px' }}>    
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2, mb: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/ProjectsPage')}>
              {t('cancel')}
            </Button>
          </Box>
          <Paper elevation={3} sx={{ p: 2, mb: 3, background:'#fed9b7' }}>
            <Typography variant="h5" component="h1" gutterBottom>
              {t('dashboard.title')}
            </Typography>
            <Typography variant="body1">
              {t('projectname')}: {projectCode}
            </Typography>
          </Paper>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label={t('report.tabs.dataGrid')} />
              <Tab label={t('report.tabs.charts')} />
            </Tabs>
            <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 3 }}>
              {/* TAB 0: DataGrid */}
              {tabValue === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 2, height: '300px' }}>
                    <Paper elevation={3} sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        {t('availableColumns.title')}
                      </Typography>
                      <AvailableColumnsList
                        availableColumns={availableColumns}
                        onAddColumn={addColumn}
                      />
                    </Paper>
                    <Paper elevation={3} sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        {t('selectedColumns.title')}
                      </Typography>
                      <SelectedColumnsList
                        selectedColumns={selectedColumns}
                        moveColumn={moveColumn}
                        removeColumn={removeColumn}
                      />
                    </Paper>
                  </Box>
                  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {reportData.length ? (
                      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <DataGrid
                          rows={selectedColumns.length > 0 ? reportData : []}
                          columns={dataGridColumns}
                          pageSize={20}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                          filterModel={filterModel}
                          onFilterModelChange={(model) => setFilterModel(model)}
                          getRowId={getRowId}
                          sx={{
                            height: '100%',
                            '& .MuiDataGrid-cell': {
                              whiteSpace: 'normal',
                              lineHeight: 'normal !important',
                            },
                          }}
                          disableColumnMenu
                          components={{
                            Toolbar: GridToolbar,
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2, mb: 2 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => exportToExcel(reportData, dataGridColumns, 'report.xlsx')}
                          >
                            {t('report.exportToExcel')}
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="h6" color="error">
                        {t('report.noData')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              {/* TAB 1: Charts */}
              {tabValue === 1 && (
                <ChartsTab 
                  reportData={reportData} 
                  allColumns={allGridColumns}
                  arrayFields={arrayFields}
                />
              )}
            </Box>
          </Box>
        </Container>
      </Box>
    </DndProvider>
  );
}